import Ably from "ably";
import { apiKey } from "./urlEndPoints";

/**
 * Single shared Ably Realtime client for the whole driver app.
 *
 * Why this exists:
 *  - The app previously created several independent `new Ably.Realtime()` clients
 *    (videoCallHelper's `getSharedRealtime`, App.js's `getSharedDriverAppRealtime`,
 *    etc.). Each is a separate connection. On Android's WebView, running two clients
 *    that both subscribe to `pharmacy-notifications` produced duplicate, competing
 *    connections that churned (connect -> drop -> reconnect) and frequently sat in a
 *    disconnected/suspended state, so live handover pop-ups were missed. iOS's
 *    WKWebView tolerated the churn; Android's WebView did not.
 *  - The installed SDK (ably 2.0.3) defaults its primary realtime host to the retired
 *    `realtime.ably.io` (now resolves to 0.0.0.0), forcing the fragile comet transport.
 *
 * This module returns ONE client, pinned to Ably's current primary + fallback hosts,
 * with fast reconnection so a brief drop recovers quickly instead of stalling.
 */

let sharedClient = null;

const isTerminalState = (client) => {
  const state = `${client?.connection?.state ?? ""}`.toLowerCase();
  return state === "closed" || state === "failed";
};

export const getAblyRealtime = () => {
  if (!apiKey) {
    return null;
  }

  if (!sharedClient || isTerminalState(sharedClient)) {
    // Close any lingering client before replacing it so we never leave an orphaned
    // connection open (the previous duplicate-connection bug).
    if (sharedClient) {
      try {
        sharedClient.close();
      } catch (error) {
        // ignore
      }
      sharedClient = null;
    }

    sharedClient = new Ably.Realtime({
      key: apiKey,
      // The SDK's legacy default host (realtime.ably.io) is retired; pin the current
      // Ably primary + fallback endpoints so the WebView keeps a stable WebSocket.
      realtimeHost: "main.realtime.ably.net",
      restHost: "main.ably.net",
      fallbackHosts: [
        "a.ably-realtime.com",
        "b.ably-realtime.com",
        "c.ably-realtime.com",
        "d.ably-realtime.com",
        "e.ably-realtime.com",
      ],
      // Recover quickly from the brief drops seen on Android WebView instead of
      // sitting dormant in disconnected/suspended for the default (15s/30s).
      disconnectedRetryTimeout: 5000,
      suspendedRetryTimeout: 8000,
    });

    sharedClient.connection.on((stateChange) => {
      if (["failed", "closed"].includes(stateChange.current)) {
        try {
          sharedClient?.close();
        } catch (error) {
          // ignore
        }
        sharedClient = null;
      }
    });
  }

  return sharedClient;
};

export default getAblyRealtime;
